// ─── Azure DevOps work-item attachments: what may be uploaded ───────────────
// The allowlist deliberately tracks the file types Azure DevOps itself accepts
// (screenshots + common document/office/archive files), so PCP never rejects
// something ADO would take — it is deliberately broader than the PCP-local
// feature-request allowlist. Executables/scripts stay out. ADO infers the
// content type from fileName; PCP serves these back as downloads.
//
// This lives in @pcp/shared, not in the route, because the browser needs the
// SAME list: the Attachments panel's file picker sets it as `accept`, and the
// drop target screens a dropped batch with it before firing any upload. A
// second copy on the client would drift, and the drift is invisible until
// someone drops a file the server then rejects (or the picker hides a file the
// server would have taken).
/** Extensions accepted on a work-item attachment upload, without the dot. */
export const ADO_ATTACHMENT_EXTS = [
    // The image set must cover every extension TinyMCE's blob cache derives from a
    // pasted image's MIME type (jpg/gif/png/apng/avif/svg/webp/bmp/tiff) — an image
    // pasted into a comment is uploaded through this same route.
    'png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'tiff', 'tif', 'svg', 'heic', 'avif', 'apng',
    'pdf', 'txt', 'md', 'log', 'csv', 'json', 'rtf',
    'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
    'odt', 'ods', 'odp', 'zip',
];
const ADO_ATTACHMENT_EXT_SET = new Set(ADO_ATTACHMENT_EXTS);
/** Hard per-file ceiling. Matched by the route's multipart `limits.fileSize`. */
export const ADO_ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;
/** Value for a file input's `accept`. `image/*` additionally lets a mobile
 *  camera/photo picker through; the server always re-validates by extension. */
export const ADO_ATTACHMENT_ACCEPT = `${ADO_ATTACHMENT_EXTS.map((e) => `.${e}`).join(',')},image/*`;
/** Lowercased extension of a filename, or '' when it has none. Mirrors the
 *  API's `extOf` so client and server read a name the same way. */
export function adoAttachmentExt(filename) {
    const m = /\.([a-z0-9]+)$/i.exec(filename);
    return m ? m[1].toLowerCase() : '';
}
export function isAdoAttachableName(filename) {
    return ADO_ATTACHMENT_EXT_SET.has(adoAttachmentExt(filename));
}
export function adoAttachmentReject(file) {
    if (!isAdoAttachableName(file.name))
        return 'type';
    if (file.size != null && file.size > ADO_ATTACHMENT_MAX_BYTES)
        return 'size';
    if (file.size === 0)
        return 'empty';
    return null;
}
/** Split a picked/dropped batch into what will upload and what the server would
 *  refuse, so the client can report the refusals once instead of firing N
 *  requests that each come back 400. Order is preserved within each list. */
export function partitionAdoAttachments(files) {
    const accepted = [];
    const rejected = [];
    for (const file of files) {
        const reason = adoAttachmentReject(file);
        if (reason)
            rejected.push({ file, reason });
        else
            accepted.push(file);
    }
    return { accepted, rejected };
}
/** One sentence naming what was skipped and why — the toast the client shows
 *  when a drop contained files the work item can't take. */
export function describeAdoAttachmentRejects(rejected) {
    if (rejected.length === 0)
        return null;
    const reason = {
        type: 'an unsupported file type',
        size: 'larger than 25 MB',
        empty: 'empty',
    };
    if (rejected.length === 1) {
        const [only] = rejected;
        return `Skipped “${only.file.name}” — ${reason[only.reason]}.`;
    }
    const names = rejected.map((r) => `“${r.file.name}”`).join(', ');
    return `Skipped ${rejected.length} files — ${names}.`;
}
//# sourceMappingURL=ado-attachments.js.map